import React, {useEffect, useState} from 'react';
import {Table} from "react-bootstrap";
import {
    addHoursToIsoString,
    durationBetween,
    formatISODate,
    formatISOTime,
    getAllDaysBetween
} from "../../../utils/DateTimeUtills";
import {
    calculateTotalHoursPerCategory,
    correlateEvents,
    floatToDurationString,
    formatDay,
    getBriefOverviewForDay,
    getEventByCode,
    getWorkdayDuration,
    getWorkdayEnd,
    groupTransactionsPerDay,
    splitEventsPerDay
} from "./pdfUtils";
import useMyTranslation from "../../../Hooks/useMyTranslation";
import {useTranslation} from "react-i18next";
import css from "../css/PdfHoursExport.module.css"
import ConditionalRender, {Fallback} from "../../ConditionalRender";
import TotalRow from "./WeekReport/TotalRow";
import {getAllocationsForGeofences} from "../../../API/timelineApi";
import LoadingSpinner from "../../Loading/LoadingSpinner";

const WeekReportTableBriefPdf = ({msisdn, transactions, selectedWeek, events, geofenceKeys}) => {
    const t = useMyTranslation('export.pdf.weekOverviewBrief');
    const tTime = useMyTranslation('global.time');
    const tPdfComm = useMyTranslation('export.pdf.common');

    const {i18n} = useTranslation();

    const [workOrderAllocations, setWorkOrderAllocations] = useState([]);
    const [allocationsLoading, setAllocationsLoading] = useState(true);

    useEffect(() => {
        async function fetchAllocations() {
            const geofences = [...new Set(transactions.map(t => t.geofenceId))];

            if (geofences.length > 0) {
                try {
                    const response = await getAllocationsForGeofences(geofences, msisdn.msisdn, selectedWeek.startDate.toISOString(), selectedWeek.endDate.toISOString());
                    setWorkOrderAllocations(response.data);
                } catch (error) {
                    console.error(error);
                }
            }

            setAllocationsLoading(false);
        }

        fetchAllocations()
    }, [msisdn.msisdn, selectedWeek, transactions]);



    const topMid = {
        textAlign: 'center',
        verticalAlign: 'top'
    }


    const WorkdayCell = ({eventTime, reportedByEmployee, rowSpan = 1}) => {
        // m -> When the employee reports the time.
        // s -> When there is no end work day and it is past midnight.

        if (!eventTime) {
            return (
                <td style={topMid} rowSpan={rowSpan}>
                    <span>-</span>
                    <NoEndWorkdayIndicator/>
                </td>
            )
        }

        return (
            <td style={topMid} rowSpan={rowSpan}>
                <span className={'fw-bold'}>{formatISOTime(eventTime)}</span>
                <p className={'small'}>{reportedByEmployee ? '(m)' : '(s)'}</p>
            </td>
        )
    }

    const DayOverview = ({workday}) => {
        const {
            workdayStart,
            workdayEnd,
            workdayDuration,
            breakDuration, privateDuration, travelDuration, customEventsDuration, allocatedDuration, eventsDuringDay } = getBriefOverviewForDay(workday, events);

        const beginWorkday = getEventByCode(eventsDuringDay, 'begin_workday', true);
        const endWorkday = getEventByCode(eventsDuringDay, 'end_workday', false);

        const eventsWithNotes = correlateEvents(eventsDuringDay?.filter(event => event.note && event.categoryType?.toLowerCase() !== 'custom'));
        const rowSpan = workday.geofences.length + 1;


        const GeofenceComment = ({geofence, workOrderAllocation}) => {

            const sortedTransactions = [...geofence.transactions].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

            return (
                <>
                    <GeofenceName geofence={geofence}/>
                    <td style={topMid}>
                        <div className={'d-flex flex-column'}>
                            {sortedTransactions.map((transaction, index) => (
                                <span key={index}>{formatISODate(transaction.startTime, { format: 'timeOnly', useLocalTime: true })}</span>
                            ))}
                        </div>
                    </td>
                    <td style={topMid}>
                        <div className={'d-flex flex-column'}>
                            {sortedTransactions.map((transaction, index) => (
                                <>
                                    <span key={index}>{formatISOTime(transaction.endTime)}
                                        <ConditionalRender condition={!transaction.endTime && durationBetween(transaction.startTime, new Date()) < 15}>
                                        <NoEndWorkdayIndicator/>
                                    </ConditionalRender></span>
                                </>
                            ))}
                        </div>
                    </td>
                    <td style={topMid}>
                        {floatToDurationString(geofence.duration)}
                        {geofence.duration < 15 && sortedTransactions.length > 0 && !sortedTransactions[sortedTransactions.length - 1].endTime ? <NoEndWorkdayIndicator/>  : null}
                    </td>
                    {/* <td style={topMid}>{floatToDurationString(workOrderAllocation?.allocatedHours ?? 0)}</td> */}
                    <td>
                        {geofence.notes.map((note, index) => <p className={'mb-2'} key={index}>{note}</p>)}
                        {/*{workOrderAllocation?.workOrders.filter(wo => wo.note).map((workOrder, index) => (*/}
                        {/*    <p className={'mb-1'} key={index}>*/}
                        {/*        <span className={'text-decoration-underline'}>{workOrder.key}</span>*/}
                        {/*        <span>: {workOrder.note}</span>*/}
                        {/*    </p>*/}
                        {/*))}*/}
                    </td>
                </>
            )
        }

        const GeofenceName = ({geofence}) => {
            const key = geofenceKeys.find(key => key.id === geofence.id);
            const keyValue = key?.extras?.length > 0 ? key.extras[0].value : null;

            return <td><span>{geofence.name} {keyValue && `(${keyValue})`}</span></td>
        }

        const formatedDay = addHoursToIsoString(workday.date.toISOString(), 3).split('T')[0]



        return (
            <>
                <tr>
                    <DayColumn day={workday.date} rowSpan={rowSpan}/>
                    <WorkdayCell eventTime={workdayStart} reportedByEmployee={beginWorkday} rowSpan={rowSpan}/>
                    <WorkdayCell eventTime={workdayEnd} reportedByEmployee={endWorkday} rowSpan={rowSpan}/>
                    <td rowSpan={rowSpan} style={topMid}>{floatToDurationString(breakDuration)}</td>
                    <td rowSpan={rowSpan} style={{...topMid, borderRight: '5px solid white'}}>
                        {floatToDurationString(workdayDuration - breakDuration - privateDuration)}
                        {!endWorkday && formatedDay === formatDay(new Date()) ? <NoEndWorkdayIndicator/> : null}
                    </td>
                    <td rowSpan={rowSpan} style={topMid}>{floatToDurationString(travelDuration)}</td>
                    <td rowSpan={rowSpan} style={{...topMid, borderRight: '5px solid white'}}>{floatToDurationString(customEventsDuration + privateDuration)}</td>

                    <ConditionalRender condition={eventsWithNotes.length > 0}>
                        <td colSpan={4}/>
                        <td>
                            <div className={'d-flex flex-column'}>
                                {eventsWithNotes.map((event, index) => (
                                    <div key={index} className={'my-1'}>
                                        <span className={'text-decoration-underline'}>{event.name}</span>
                                        <span>: {event.note}</span>
                                    </div>
                                ))}
                            </div>
                        </td>
                    </ConditionalRender>
                </tr>
                {
                    (() => {
                        return (
                            [...workday.geofences]
                                .sort((a, b) => {
                                    const firstA = a.transactions.length > 0 ? new Date(a.transactions[0].startTime) : new Date(0);
                                    const firstB = b.transactions.length > 0 ? new Date(b.transactions[0].startTime) : new Date(0);
                                    return firstA - firstB;
                                })
                                .map(geofence => (
                                    <tr key={geofence.id}>
                                        <GeofenceComment geofence={geofence} workOrderAllocation={workOrderAllocations[formatedDay]?.find(x => x.geofenceId === geofence.id)} />
                                    </tr>
                                ))

                        )
                    })()
                }
            </>
        )
    }

    const DayColumn = ({day, rowSpan = 1}) => {

        const localizedDate = day.toLocaleDateString(i18n.language, {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        })

        return (
            <td className={`align-top ${css.dayColumn}`} rowSpan={rowSpan}>
                <div className={'d-flex flex-column'}>
                    <span className={'text-capitalize fw-bold fs-5'}>
                        {day.toLocaleDateString(i18n.language, { weekday: 'long' })}
                    </span>
                    <small className={'mt-1 fw-bold small'} style={{color: '#c9c9c9'}}>{localizedDate}</small>
                </div>
            </td>
        )
    }

    const transactionsPerDay = groupTransactionsPerDay(transactions);
    const eventsPerDay = splitEventsPerDay(events);

    const regularHeaderStyle = {
        width: '90px'
    };

    const headerWithBorder = {
        ...regularHeaderStyle,
        borderRight: '5px solid white'
    }

    const getGeneratedDateStr = () => {
        const now = new Date();

        const day = now.getDate();
        const month = now.getMonth() + 1; // Months are zero-based
        const year = now.getFullYear();
        const hours = now.getHours();
        const minutes = now.getMinutes().toString().padStart(2, '0');

        return `${day}-${month}-${year} ${hours}:${minutes}`;
    }

    if (allocationsLoading)
        return <LoadingSpinner small/>

    return (
        <>
            {/*<span className={'fw-bold'}>{t('title')}</span>*/}
            <Table bordered responsive className={css.scalable} id={css.weekReportTable}>
                <thead>
                <tr>
                    <th style={{width: '130px'}} className={`${css.tableHeader}`}>
                        <div className={'d-flex flex-column'}>
                            <span className={'fs-4 text-capitalize'}>{tTime('units.singular.week')} {selectedWeek.week}</span>
                            <span className={'fs-6'} style={{color: '#c9c9c9'}}>{selectedWeek.year}</span>
                        </div>
                    </th>
                    <th style={regularHeaderStyle} className={`text-center ${css.tableHeader}`}>
                        <div className={'d-flex flex-column'}>
                            <span>{t('startWorkday')}</span>
                            {/* <div className={'d-flex justify-content-center mt-1'}><TbClockHour8 size={22}/></div> */}
                        </div>
                    </th>
                    <th style={regularHeaderStyle} className={`text-center ${css.tableHeader}`}>
                        <div className={'d-flex flex-column'}>
                            <span>{t('endWorkday')}</span>
                            {/* <div className={'d-flex justify-content-center mt-1'}><TbClock size={22}/></div> */}
                        </div>
                    </th>
                    <th style={regularHeaderStyle} className={`text-center ${css.tableHeader}`}>{t('events.break', 'global')}</th>
                    <th style={headerWithBorder} className={`text-center ${css.tableHeader}`}>{tPdfComm('netWorkTime')}</th>
                    <th style={regularHeaderStyle} className={`text-center ${css.tableHeader}`}>{t('events.travel', 'global')}</th>
                    <th style={{...headerWithBorder, padding: '0.4rem'}} className={`text-center text-break ${css.tableHeader}`}>{t('events.other', 'global')}</th>
                    {/*<th style={regularHeaderStyle} className={`text-center ${css.tableHeader}`}>{tPdfComm('totalTimeWorkLocations')}</th>*/}
                    <th width={250} className={`text-start text-break ${css.tableHeader}`}>{tPdfComm("workLocation")}</th>
                    <th style={regularHeaderStyle} className={`text-center ${css.tableHeader}`}>{t('arrival')}</th>
                    <th style={regularHeaderStyle} className={`text-center ${css.tableHeader}`}>{t('departure')}</th>
                    <th style={{...regularHeaderStyle, padding: '0.4rem'}} className={`text-center text-break ${css.tableHeader}`}>{t("presence")}</th>
                    {/* <th style={regularHeaderStyle} className={`text-center text-break ${css.tableHeader}`}>{tPdfComm('reported')}</th> */}
                    <th className={`text-start ${css.tableHeader}`}>{tPdfComm('comments')}</th>
                </tr>
                </thead>
                <tbody>
                {
                    getAllDaysBetween(selectedWeek.startDate, selectedWeek.endDate).map((day, index) => {
                        const workday = transactionsPerDay[formatDay(day)];
                        const eventsForToday = eventsPerDay[formatDay(day)];

                        if (!workday && !eventsForToday)
                                return (
                                    <tr key={day}>
                                        <DayColumn day={day}/>
                                        {[...Array(3).keys()].map((_, i) => <td key={i} className={'text-center'}>-</td>)}
                                        <td className={'text-center'} style={{borderRight: '5px solid white'}}>-</td>
                                        <td className={'text-center'}>-</td>
                                        <td className={'text-center'} style={{borderRight: '5px solid white'}}>-</td>
                                        {[...Array(5).keys()].map((_, i) => <td key={i} className={'text-center'}>-</td>)}

                                    </tr>
                                )

                            else if (workday)
                            {
                                const { totalDuration, geofenceNames, firstInTime, lastOutTime, geofences } = workday;
                                const workLocations = Array.from(geofenceNames);

                                return (
                                    <DayOverview key={index} workday={{
                                        date: day,
                                        firstInTime: firstInTime,
                                        lastOutTime: lastOutTime ?? addHoursToIsoString(firstInTime, 15),
                                        totalWorkLocations: totalDuration,
                                        workLocations,
                                        geofences
                                    }}/>
                                );
                            }
                            else {
                                const workdayFromEvents = {
                                    startWorkday: getEventByCode(eventsForToday, 'begin_workday', true),
                                    endWorkday: getEventByCode(eventsForToday, 'end_workday', false),
                                    breakDuration: calculateTotalHoursPerCategory(eventsForToday, "break"),
                                    privateDuration: calculateTotalHoursPerCategory(eventsForToday, "private"),
                                    travelDuration: calculateTotalHoursPerCategory(eventsForToday, "travel"),
                                }


                                const eventsWithNotes = correlateEvents(eventsForToday?.filter(event => event.note && event.categoryType?.toLowerCase() !== 'custom'));

                                const formatedDay = addHoursToIsoString(day.toISOString(), 3).split('T')[0]
                                const endWorkday = getWorkdayEnd(workdayFromEvents.endWorkday, formatedDay);

                                const workdayDuration = getWorkdayDuration(day, workdayFromEvents.startWorkday, workdayFromEvents.endWorkday);
                                
                                return (
                                    <tr key={day}>
                                        <DayColumn day={day}/>
                                        <WorkdayCell eventTime={workdayFromEvents.startWorkday?.eventTime} reportedByEmployee={workdayFromEvents.startWorkday}/>
                                        <WorkdayCell eventTime={endWorkday} reportedByEmployee={workdayFromEvents.endWorkday}/>
                                        <td className={'text-center'}>{floatToDurationString(workdayFromEvents.breakDuration)}</td>
                                        <td className={'text-center'} style={{borderRight: '5px solid white'}}>
                                            {floatToDurationString(workdayDuration - workdayFromEvents.breakDuration - workdayFromEvents.privateDuration)}
                                            {!workdayFromEvents.endWorkday && formatedDay === formatDay(new Date()) ? <NoEndWorkdayIndicator/> : null}
                                        </td>
                                        <td className={'text-center'}>{floatToDurationString(workdayFromEvents.travelDuration)}</td>
                                        <td className={'text-center'} style={{borderRight: '5px solid white'}}>{floatToDurationString(calculateTotalHoursPerCategory(eventsForToday, "custom") + calculateTotalHoursPerCategory(eventsForToday, "workLocation") + calculateTotalHoursPerCategory(eventsForToday, "private"))}</td>
                                        <td className={'text-center'}>-</td>
                                        <td className={'text-center'}>-</td>
                                        <td className={'text-center'}>-</td>
                                        <td className={'text-center'}>-</td>
                                        <ConditionalRender condition={eventsWithNotes.length > 0}>
                                            <Fallback>
                                                <td className={'text-center'}></td>
                                            </Fallback>
                                            <td>
                                                {eventsWithNotes.map((event, index) => (
                                                    <div key={index} className={'my-1'}>
                                                        <span className={'text-decoration-underline'}>{event.name}</span>
                                                        <span>: {event.note}</span>
                                                    </div>
                                                ))}
                                            </td>
                                        </ConditionalRender>
                                    </tr>
                                )
                        }
                    })
                }
                <TotalRow allEvents={events} transactionsPerDay={transactionsPerDay} workOrderAllocations={workOrderAllocations}/>
                </tbody>
            </Table>
            <div className={'d-flex justify-content-between'}>
                <div>
                    <p className={'mb-1 small'}>(s) - Salvo</p>
                    <p className={'mb-1 small'}>(m) - {t('employee')}</p>
                    <p className={'mb-1 small'}><span className={'fs-6'}>*</span> - {t('workdayNotFinished')}</p>
                </div>
                <span className={'small fw-bold'}>{t('reportGeneratedAt')}: {getGeneratedDateStr()}</span>
            </div>

        </>
    );
};

export const NoEndWorkdayIndicator = () => {
    return <span className={'fs-6'}> *</span>
}

export default WeekReportTableBriefPdf;